"use client";

import { useEffect, useState } from "react";
import Logo from "@/app/components/Logo";
import Button from "@/app/components/Button";
import TextAreaField from "@/app/components/TextAreaField";
import { useConfirm } from "@/app/components/ConfirmDialog";
import { useGateHold } from "@/app/components/Gate";
import { AccountView } from "@/lib/types";
import { Detail } from "./characterViews";
import { PLAY_CSS } from "./playStyle";
import PlayProfile from "./PlayProfile";
import PlayNow from "./PlayNow";
import PlayStatus from "./PlayStatus";
import PlayInventory from "./PlayInventory";
import PlaySkills from "./PlaySkills";
import PlayRelations from "./PlayRelations";

const WORLD = "Baunheim";
const REWORK_MAX = 2000;
/** 手直しの相手の鍵。本人 */
const SELF = "self";

/** 出ている手直しの依頼。fixed は、整備が始まっていて替えられない依頼 */
type ReworkRow = { key: string; id: string; text: string; fixed: boolean };

type Tab = "profile" | "status" | "inventory" | "skills" | "relations";

const TABS: { tab: Tab; label: string }[] = [
  { tab: "profile", label: "プロフィール" },
  { tab: "status", label: "ステータス" },
  { tab: "inventory", label: "インベントリ" },
  { tab: "skills", label: "スキル" },
  { tab: "relations", label: "関係" },
];

/**
 * 作成を終えた Baunheim のキャラを確かめ、決定か手直しを選ぶ画面。
 * npc を渡したときは、管理者用画面から master の NPC を扱う。stage が pending なら確認待ちの依頼、master なら master に在る NPC を開く
 */
export default function CharacterReview({
  region,
  slug,
  onBack,
  onDone,
  npc,
}: {
  region: string;
  slug: string;
  account: AccountView;
  onBack: () => void;
  onDone: (account?: AccountView) => void;
  npc?: { stage: "pending" | "master"; key: string };
}) {
  const detailUrl = npc
    ? `/api/admin/npc/detail?world=${WORLD}&stage=${npc.stage}&key=${encodeURIComponent(npc.key)}`
    : `/api/character/detail?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`;
  const canDecide = !npc || npc.stage === "pending";
  const [detail, setDetail] = useState<Detail | null>(null);
  const [tab, setTab] = useState<Tab>("profile");
  const [reworking, setReworking] = useState(false);
  const [text, setText] = useState("");
  // PC と同行者あての、出ている手直しの依頼と、欄の文。鍵は本人が self、同行者がスラッグ
  const [rows, setRows] = useState<ReworkRow[]>([]);
  const [texts, setTexts] = useState<Record<string, string>>({});
  // 手直しの欄を畳んでいるか。畳んでも欄の文は残る
  const [folded, setFolded] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [ask, confirmDialog] = useConfirm();
  // 中身を見ている同行者のスラッグ。本人を見ている間は null
  const [who, setWho] = useState<string | null>(null);
  const [companions, setCompanions] = useState<Record<string, Detail>>({});
  const [companionMessage, setCompanionMessage] = useState("");
  // キャラクターの中身を読み終えるまで、ロード画面に待ってもらう
  useGateHold(detail === null && message === "");

  const open = (target: string | null) => {
    setTab("profile");
    setWho(target);
    setCompanionMessage("");
    if (target === null || companions[target]) return;
    fetch(`/api/character/detail?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(target)}&owner=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (response.ok) setCompanions((current) => ({ ...current, [target]: body as Detail }));
        else setCompanionMessage(body.error ?? "同行者を読めません");
      })
      .catch(() => setCompanionMessage("サーバーに繋がりません"));
  };

  useEffect(() => {
    let alive = true;
    fetch(detailUrl)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setDetail(body as Detail);
        else setMessage(body.error ?? "キャラクターを読めません");
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [detailUrl]);

  // PC の確認では、出ている手直しの依頼を読み、欄に入れる
  const isNpc = Boolean(npc);
  useEffect(() => {
    if (isNpc) return;
    let alive = true;
    fetch(`/api/character/rework?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (!response.ok) {
          setMessage(body.error ?? "手直しの依頼を読めません");
          return;
        }
        const read = body.rows as ReworkRow[];
        setRows(read);
        setTexts(Object.fromEntries(read.map((row) => [row.key, row.text])));
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [isNpc, region, slug]);

  const post = async (url: string, body: Record<string, unknown>) => {
    setSending(true);
    setMessage("");
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (response.ok && result.ok) {
        onDone(result.account as AccountView | undefined);
      } else {
        setMessage(result.error ?? "送れません");
      }
    } catch {
      setMessage("サーバーに繋がりません");
    } finally {
      setSending(false);
    }
  };

  const decide = async () => {
    if (sending) return;
    if (!(await ask(npc ? "この NPC で決定し、master へ入れます。" : "このキャラクターで決定します。決定した後は手直しを依頼できません。"))) return;
    if (npc) await post("/api/admin/npc/decide", { world: WORLD, key: npc.key });
    else await post("/api/character/decide", { world: WORLD, region, slug });
  };

  const rework = async () => {
    if (sending || text.trim() === "") return;
    if (!(await ask("この内容で手直しを依頼します。", { ok: "依頼する" }))) return;
    await post("/api/admin/npc/rework", { world: WORLD, stage: npc?.stage, key: npc?.key, text });
  };

  // PC の確認では、同行者の中身も見られる。NPC の形では並べない
  const party = npc ? [] : detail?.companions ?? [];
  const shown = who === null ? detail : companions[who] ?? null;

  // PC の手直しの相手。本人と同行者。依頼は相手ごとに 1 つ
  const targets = detail && !npc ? [{ key: SELF, name: detail.name }, ...party.map((one) => ({ key: one.slug, name: one.name }))] : [];
  const rowOf = (key: string) => rows.find((row) => row.key === key);
  const editable = targets.filter((one) => !rowOf(one.key)?.fixed);
  const written = (key: string) => (texts[key] ?? "").trim();
  const pending = rows.length > 0;
  const changed = editable.some((one) => written(one.key) !== (rowOf(one.key)?.text ?? ""));
  const empty = editable.every((one) => written(one.key) === "");

  const sendTexts = async (clear: boolean) => {
    const body: Record<string, string> = {};
    for (const one of editable) body[one.key] = clear ? "" : written(one.key);
    await post("/api/character/rework", { world: WORLD, region, slug, texts: body });
  };

  const requestRework = async () => {
    if (sending || empty || !changed) return;
    if (!(await ask(pending ? "この内容に手直しの依頼を書き替えます。" : "この内容で手直しを依頼します。", { ok: pending ? "書き替える" : "依頼する" }))) return;
    await sendTexts(false);
  };

  const withdraw = async () => {
    if (sending) return;
    if (!(await ask("手直しの依頼を取り下げます。", { ok: "取り下げる", danger: true }))) return;
    await sendTexts(true);
  };

  return (
    <div className="panel panel-wide panel-scene panel-create">
      <Logo />
      <div className="head">
        <span className="head-user">
          {WORLD} ／ {npc ? "master の NPC" : region}
          <strong>{detail ? detail.name : slug}</strong>
        </span>
        <Button onClick={onBack} inline disabled={sending}>
          戻る
        </Button>
      </div>

      {detail ? (
        <>
          <style dangerouslySetInnerHTML={{ __html: PLAY_CSS }} />
          <div className="pl-scope">
            {party.length > 0 ? (
              <div className="pl-tabs pl-tabs-people">
                <button type="button" className={who === null ? "pl-tab pl-tab-on" : "pl-tab"} aria-pressed={who === null} onClick={() => open(null)}>
                  {detail.name}
                </button>
                {party.map((one) => (
                  <button
                    key={one.slug}
                    type="button"
                    className={who === one.slug ? "pl-tab pl-tab-on" : "pl-tab"}
                    aria-pressed={who === one.slug}
                    onClick={() => open(one.slug)}
                  >
                    {one.name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="pl-tabs pl-tabs-folder" role="tablist">
              {TABS.filter((one) => one.tab !== "relations" || Boolean(npc) || who !== null).map((one) => (
                <button
                  key={one.tab}
                  type="button"
                  role="tab"
                  aria-selected={tab === one.tab}
                  className={tab === one.tab ? "pl-tab pl-tab-on" : "pl-tab"}
                  onClick={() => setTab(one.tab)}
                >
                  {one.label}
                </button>
              ))}
            </div>
            <div className="pl-folder-body">
              {shown ? (
                <>
                  {tab === "profile" ? <PlayProfile text={shown.profile ?? ""} /> : null}
                  {tab === "status" && !shown.status ? <p className="pl-note">ステータスを持たないキャラクターです。</p> : null}
                  {tab === "status" ? <PlayNow detail={shown} /> : null}
                  {tab === "status" && shown.status ? <PlayStatus key={shown.slug} status={shown.status} /> : null}
                  {tab === "inventory" ? <PlayInventory key={shown.slug} detail={shown} /> : null}
                  {tab === "skills" ? <PlaySkills key={shown.slug} detail={shown} /> : null}
                  {tab === "relations" && (npc || who !== null) ? <PlayRelations key={shown.slug} relations={shown.relations ?? []} /> : null}
                </>
              ) : (
                <p className="pl-note">{companionMessage || "読み込み中・・・"}</p>
              )}
            </div>
          </div>

          {!npc && (reworking || pending) ? (
            <section className="section">
              <h2 className="section-title">
                手直しの依頼
                <Button inline onClick={() => setFolded(!folded)}>
                  {folded ? "ひらく" : "たたむ"}
                </Button>
              </h2>
              <p className="section-note">
                {pending
                  ? "手直しの依頼中です。整備が始まるまで、書き替えと取り下げができます。"
                  : "直したい相手の欄に、どこをどう直したいかを書いてください。書いた相手の分だけ依頼します。"}
              </p>
              {folded ? null : targets.map((one) => (
                <TextAreaField
                  key={one.key}
                  id={`review-rework-${one.key}`}
                  label={one.name}
                  value={texts[one.key] ?? ""}
                  onChange={(value) => setTexts((current) => ({ ...current, [one.key]: value }))}
                  rows={5}
                  max={REWORK_MAX}
                  note={rowOf(one.key)?.fixed ? "整備が始まっています。" : undefined}
                  disabled={sending || rowOf(one.key)?.fixed === true}
                />
              ))}
              <div className="actions" style={folded ? { display: "none" } : undefined}>
                <Button variant="primary" onClick={requestRework} disabled={sending || empty || !changed}>
                  {sending ? "送っています" : pending ? "依頼を書き替える" : "手直しを依頼する"}
                </Button>
                {pending ? (
                  <Button variant="danger" onClick={withdraw} disabled={sending || !rows.some((row) => !row.fixed)}>
                    依頼を取り下げる
                  </Button>
                ) : (
                  <Button onClick={() => setReworking(false)} disabled={sending}>
                    やめる
                  </Button>
                )}
              </div>
            </section>
          ) : reworking ? (
            <section className="section">
              <h2 className="section-title">手直しの依頼</h2>
              <TextAreaField
                id="review-rework"
                label="どこをどう直したいかを書いてください"
                value={text}
                onChange={setText}
                rows={8}
                max={REWORK_MAX}
                disabled={sending}
              />
              <div className="actions">
                <Button variant="primary" onClick={rework} disabled={sending || text.trim() === ""}>
                  {sending ? "送っています" : "手直しを依頼する"}
                </Button>
                <Button onClick={() => setReworking(false)} disabled={sending}>
                  やめる
                </Button>
              </div>
            </section>
          ) : (
            <div className="actions">
              {canDecide && !pending ? (
                <Button variant="primary" onClick={decide} disabled={sending}>
                  {npc ? "この NPC で決定" : "このキャラクターで決定"}
                </Button>
              ) : null}
              <Button onClick={() => setReworking(true)} disabled={sending}>
                手直しを依頼する
              </Button>
            </div>
          )}
        </>
      ) : null}

      <p className={message ? "message" : "message message-info"}>{message || (detail ? "" : "読み込み中・・・")}</p>
      {confirmDialog}
    </div>
  );
}
